/**
 * Job replay timeline — turn-by-turn review for supervisors.
 *
 * Pulls `GET /api/jobs/{id}/steps` and renders each step as a row with
 * the model's action, target selector, input text (trimmed), error
 * message, timing, and tokens. No screenshots in v1 (blob SAS flow is
 * a follow-up); the text record is already enough to catch most bad
 * behavior during break-in.
 *
 * Loading / empty / error / permission-denied states per Rule Zero #8.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  RotateCw,
  Shield,
} from "lucide-react";

import { ApiError } from "@/lib/apiClient";

import { type JobStep, useJobSteps } from "./replayApi";


function formatMoment(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  return new Date(iso).toLocaleTimeString();
}


function stepTone(step: JobStep): "neutral" | "ok" | "warn" | "fail" {
  if (step.errorMessage) return "fail";
  if (step.stepType === "scope_violation" || step.stepType === "model_error") return "fail";
  if (step.stepType === "training_checkpoint") return "warn";
  if (step.stepType === "phase_failed") return "fail";
  if (step.stepType === "phase_complete") return "ok";
  return "neutral";
}


const TONE_CLS: Record<"neutral" | "ok" | "warn" | "fail", string> = {
  neutral: "border-paper-line bg-paper-0",
  ok: "border-status-success/30 bg-status-success-bg",
  warn: "border-status-approval/30 bg-status-approval-bg",
  fail: "border-status-failed/30 bg-status-failed/5",
};


export function ReplayScreen() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const { data, isLoading, error, refetch } = useJobSteps(jobId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-line bg-paper-0 px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 rounded-sm px-2 py-1 text-sm text-ink-2 transition-colors hover:bg-paper-tint"
          >
            <ArrowLeft size={14} aria-hidden />
            Back
          </button>
          <div>
            <h1 className="text-lg font-semibold text-ink-1">Replay</h1>
            <p className="text-xs text-ink-3">
              {jobId ? <span className="font-mono">{jobId}</span> : "—"} · turn-by-turn
              audit of the agent's actions
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm text-ink-2 hover:bg-paper-1"
        >
          <RotateCw size={14} />
          Refresh
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading && (
          <div className="flex items-center gap-2 py-12 text-ink-3">
            <Loader2 className="animate-spin" size={18} />
            Loading replay…
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-start gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-4 text-sm">
            <AlertCircle className="text-status-failed" size={18} />
            <div>
              <div className="font-medium text-ink-1">Couldn't load replay.</div>
              <div className="text-ink-3">{(error as Error).message}</div>
              {error instanceof ApiError && error.correlationId && (
                <div className="mt-1 font-mono text-[10px] text-ink-3">
                  {error.correlationId}
                </div>
              )}
            </div>
          </div>
        )}

        {!isLoading && !error && (data?.items?.length ?? 0) === 0 && (
          <div className="rounded-md border border-dashed border-paper-line p-8 text-center text-ink-3">
            <Shield className="mx-auto mb-2" size={24} />
            No turns logged yet. Steps appear as the agent runs.
          </div>
        )}

        {!isLoading && !error && (data?.items?.length ?? 0) > 0 && (
          <ol className="space-y-2">
            {data!.items.map((s) => (
              <StepRow
                key={s.id}
                step={s}
                expanded={expanded.has(s.id)}
                onToggle={() => toggle(s.id)}
              />
            ))}
          </ol>
        )}
      </main>
    </div>
  );
}


function StepRow(props: {
  step: JobStep;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { step } = props;
  const tone = stepTone(step);
  const hasDetail =
    step.actionInputText !== null ||
    step.actionTargetSelector !== null ||
    step.errorMessage !== null ||
    step.pageDomHash !== null ||
    step.promptBlobRef !== null;

  return (
    <li className={`rounded-md border px-3 py-2 text-sm ${TONE_CLS[tone]}`}>
      <button
        type="button"
        onClick={hasDetail ? props.onToggle : undefined}
        className={`flex w-full items-start gap-2 text-left ${hasDetail ? "cursor-pointer" : "cursor-default"}`}
      >
        <span className="w-4 pt-0.5 text-ink-3">
          {hasDetail ? (
            props.expanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )
          ) : null}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-[11px] text-ink-3">
              T{String(step.turnNumber).padStart(3, "0")}
            </span>
            <span className="font-medium text-ink-1">{step.actionTaken ?? step.stepType}</span>
            {step.actionKind && step.actionKind !== step.actionTaken && (
              <span className="rounded-sm bg-paper-1 px-1.5 py-0.5 text-[10px] text-ink-3">
                {step.actionKind}
              </span>
            )}
            {step.stepType === "training_checkpoint" && (
              <span className="rounded-sm bg-status-approval/10 px-1.5 py-0.5 text-[10px] text-status-approval">
                training
              </span>
            )}
            {step.tookRetry && (
              <span className="rounded-sm bg-status-approval/10 px-1.5 py-0.5 text-[10px] text-status-approval">
                retry
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
            <span>{formatMoment(step.occurredAt)}</span>
            {step.modelUsed && <span>· {step.modelUsed}</span>}
            {(step.inputTokens || step.outputTokens) && (
              <span>
                · {step.inputTokens ?? 0}in / {step.outputTokens ?? 0}out tok
              </span>
            )}
            {step.errorMessage && (
              <span className="text-status-failed">· {step.errorMessage.slice(0, 140)}</span>
            )}
          </div>
        </div>
      </button>

      {hasDetail && props.expanded && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pl-6 text-[12px] text-ink-2">
          {step.actionTargetSelector && (
            <>
              <dt className="text-ink-3">Selector</dt>
              <dd className="font-mono text-[11px] break-all">{step.actionTargetSelector}</dd>
            </>
          )}
          {(step.actionTargetX !== null || step.actionTargetY !== null) && (
            <>
              <dt className="text-ink-3">Coords</dt>
              <dd className="font-mono text-[11px]">
                ({step.actionTargetX ?? "—"}, {step.actionTargetY ?? "—"})
              </dd>
            </>
          )}
          {step.actionInputText && (
            <>
              <dt className="text-ink-3">Typed</dt>
              <dd className="font-mono text-[11px] break-all">{step.actionInputText}</dd>
            </>
          )}
          {step.pageDomHash && (
            <>
              <dt className="text-ink-3">DOM hash</dt>
              <dd className="font-mono text-[11px]">{step.pageDomHash}</dd>
            </>
          )}
          {step.modelConfidence !== null && (
            <>
              <dt className="text-ink-3">Confidence</dt>
              <dd>{(step.modelConfidence * 100).toFixed(1)}%</dd>
            </>
          )}
          {step.errorMessage && (
            <>
              <dt className="text-ink-3">Error</dt>
              <dd className="text-status-failed">{step.errorMessage}</dd>
            </>
          )}
          {step.screenshotBlobRef && (
            <>
              <dt className="text-ink-3">Screenshot</dt>
              <dd className="font-mono text-[11px] text-ink-3">{step.screenshotBlobRef}</dd>
            </>
          )}
        </dl>
      )}
    </li>
  );
}
